/**
 * `pettyCash.*` — anchor procedures for the petty-cash surface (S30/S33).
 *
 * Phase 1a ships ONE procedure: `anchorData` — the single-round-trip
 * composite aggregator the SPA's `/petty-cash` page calls on mount. It
 * mirrors `briefings.workspaceSignals`'s pattern: parallel domain reads
 * server-side, one wire roundtrip, flat shape so the SPA doesn't pick
 * through nested unions.
 *
 * @see docs/adr/0012-bank-accounts-and-cash-movements.md §10 (Imprest System)
 * @see prototype/cash/petty-cash.html
 */
import { z } from "zod";
import { denominationCount, moneyPaise, signedPaise, uuid } from "../_common.js";

/**
 * The 8-value `expense_category` enum from `cash_movements`. Closed for
 * V1 (ADR-0012 §10 amendment Q7).
 */
const expenseCategory = z.enum([
  "postage",
  "conveyance",
  "travel",
  "refreshments",
  "office_supplies",
  "printing_stationery",
  "repairs_maintenance",
  "sundries",
]);

export const anchorDataInput = z.object({
  /**
   * Optional drawer override. V1 ships single-drawer per tenant; when
   * omitted the server picks the first active `type=cash` account. The
   * field is here so the multi-drawer expansion (V1.x) lands without a
   * wire-contract churn.
   */
  drawerId: uuid.optional(),
});

export const anchorDataOutput = z.object({
  drawer: z.object({
    id: uuid,
    name: z.string(),
    imprestPaise: moneyPaise,
    /**
     * Running balance — current book balance of the drawer. Signed because
     * an over-balance petty_cash_expense post (pre-guard) can leave the
     * drawer below zero. Domain now refuses such posts (ADR-0012 §10.4 —
     * Imprest System), but historical rows may still produce a negative.
     */
    balancePaise: signedPaise,
    lastCountedAt: z.string().nullable(),
    daysSinceLastCount: z.number().int().nonnegative().nullable(),
    /** Custodian + operator are V1.x; null in V1.0 until membership tagging lands. */
    custodian: z.string().nullable(),
    operator: z.string().nullable(),
  }),
  /**
   * Tenant-configurable founder-approval threshold from
   * `companies.approval_threshold_paise` (ADR-0012 §2 Q2). The composer
   * substitutes this into the "vouchers ≤ ₹X post straight" tip and the
   * live amount-input helper flag — so the threshold is one wire field
   * away from the SPA instead of hardcoded.
   */
  approvalThresholdPaise: moneyPaise,
  thisMonth: z.object({
    voucherCount: z.number().int().nonnegative(),
    totalSpentPaise: moneyPaise,
    topCategory: z
      .object({
        category: expenseCategory,
        paise: moneyPaise,
        /** Share of total spend in this month, 0-100. */
        percent: z.number().min(0).max(100),
      })
      .nullable(),
    perCategory: z.array(
      z.object({
        category: expenseCategory,
        totalPaise: moneyPaise,
        voucherCount: z.number().int().nonnegative(),
      }),
    ),
  }),
  recentCounts: z
    .array(
      z.object({
        id: uuid,
        /** ISO date — `value_date` of the cash-count row, or audit `at` date. */
        countedAt: z.string(),
        variancePaise: signedPaise,
        outcome: z.enum(["exact", "short", "over"]),
        postedBy: z.string().nullable(),
        /**
         * `'movement'` — variance-bearing row from `cash_movements`.
         * `'audit'`    — zero-variance count read from `audit_entries`
         *                (verb='counted'). Lets the SPA render those rows
         *                in a subtle muted style (item 1d closeout).
         */
        source: z.enum(["movement", "audit"]),
      }),
    )
    .max(6),
  /** `cash_movements` on this drawer flagged for founder review + still pending. */
  awaitingFounderReview: z.number().int().nonnegative(),
});

/**
 * `pettyCash.countsHistory` — merged history for the count-history page.
 *
 * Composes variance-bearing counts from `cash_movements` with
 * zero-variance counts from `audit_entries.verb='counted'`. The history
 * page used to only see variance rows because `bankAccounts.markCounted`
 * doesn't write to `cash_movements`; this procedure closes that gap
 * without disturbing `cashMovements.list`'s narrower contract.
 */
export const countsHistoryInput = z.object({
  /** `type=cash` drawer id. */
  accountId: uuid,
  /** ISO date — inclusive lower bound on `countedAt`. Optional. */
  fromDate: z.string().optional(),
  /** Page size; default 100. */
  limit: z.number().int().min(1).max(200).optional(),
});

export const countsHistoryOutput = z.object({
  rows: z.array(
    z.object({
      id: uuid,
      /**
       * ISO date (YYYY-MM-DD) of the count — `value_date` for movement
       * rows, `at` (date-portion) for audit rows. Kept for the SPA's
       * day-grouping; prefer `postedAt` for precise audit-trail display.
       */
      countedAt: z.string(),
      variancePaise: signedPaise,
      outcome: z.enum(["exact", "short", "over"]),
      narration: z.string(),
      source: z.enum(["movement", "audit"]),
      /**
       * Full ISO datetime when the count was posted — drives the
       * "Posted at" line in the expanded audit-trail panel with locale
       * `toLocaleString()` rendering.
       */
      postedAt: z.string(),
      /** Display name of the posting actor; null when not a user or when the user row is hidden. */
      postedByName: z.string().nullable(),
      /** Raw actor id (uuid) so the SPA can deep-link into the audit log. */
      postedById: z.string().nullable(),
      /**
       * Book balance at the moment of the count, in signed paise. Null
       * for legacy rows that didn't capture the metadata; the SPA shows
       * a "Legacy count — denominations not recorded" muted note.
       */
      bookBalancePaise: signedPaise.nullable(),
      /** Total counted (sum of pieces × denomination), in signed paise. Null for legacy. */
      countedPaise: signedPaise.nullable(),
      /** Per-denomination piece counts the operator recorded. Null for legacy rows. */
      denominations: denominationCount.nullable(),
    }),
  ),
});

export type AnchorDataInput = z.infer<typeof anchorDataInput>;
export type AnchorDataOutput = z.infer<typeof anchorDataOutput>;
export type CountsHistoryInput = z.infer<typeof countsHistoryInput>;
export type CountsHistoryOutput = z.infer<typeof countsHistoryOutput>;
